/**
 * @fileoverview Signal Type Definitions
 * Defines the generic SignalDefinition contract for all signals in CAP.IO.
 * @module signals/types
 *
 * CONTRACT:
 * All signals must implement the SignalDefinition interface.
 * The `compute` function must be pure: same inputs always yield same outputs.
 * Signals must validate input series using `isValidSeries` and throw descriptive
 * errors if validation fails.
 */

/**
 * A single point in a time series.
 * Must be sorted oldest → newest when passed to signals.
 * @typedef {Object} TimeseriesPoint
 * @property {string} t - ISO 8601 date string (e.g., "2025-01-15")
 * @property {number} v - Numeric value at this point in time (must not be NaN)
 */

/**
 * Generic input structure for signals that operate on a time series.
 * @typedef {Object} SignalSeriesInput
 * @property {TimeseriesPoint[]} series - Array of timeseries points, ordered oldest to newest
 */

/**
 * Generic signal definition interface.
 *
 * @template TInput - The input type for the signal (must include a series or relevant data)
 * @template TOutput - The output type produced by the signal's compute function
 *
 * @typedef {Object} SignalDefinition
 * @property {string} id - Unique identifier for the signal (snake_case, e.g., "dxy_trend")
 * @property {string} name - Human-readable display name
 * @property {string} description - Clear description of what the signal measures and how
 * @property {string} category - Signal category for grouping (e.g., "liquidity", "volatility")
 * @property {function(TInput): TOutput} compute - Pure computation function.
 *   - MUST validate inputs before processing
 *   - MUST throw an Error with signal ID in message if input is invalid
 *   - MUST return deterministic output for same inputs
 *   - MUST NOT have side effects
 */

/**
 * Stress regime classification.
 * Used to categorize the current market stress level.
 * @typedef {"CALM"|"NORMAL"|"WARNING"|"STRESSED"|"CRISIS"} StressRegime
 */

/**
 * Standardized signal reading output.
 * All signals can optionally expose this format for composite signal construction.
 *
 * @typedef {Object} SignalReading
 * @property {string} id - Signal identifier
 * @property {number} score - Normalized stress score (0-100)
 * @property {StressRegime} regime - Stress regime classification
 * @property {number} raw - Domain-native raw value (e.g., bps, YoY %, index level)
 * @property {number|null} delta - Change vs prior period (null if unavailable)
 * @property {string} lastUpdated - ISO 8601 timestamp of the reading
 * @property {Object} [meta] - Additional signal-specific metadata
 */

/**
 * Error thrown when a signal receives invalid input.
 * @typedef {Object} SignalValidationError
 * @property {string} signalId - The signal that failed validation
 * @property {string} message - Descriptive error message
 */

/**
 * Validates that a series is non-empty and has valid structure.
 *
 * A valid series must:
 * - Be a non-null array
 * - Have at least one element
 * - Each element must have a string `t` (timestamp) property
 * - Each element must have a numeric `v` (value) property that is not NaN
 *
 * @param {TimeseriesPoint[]} series - Series to validate
 * @returns {boolean} True if valid, false otherwise
 *
 * @example
 * // Valid series
 * isValidSeries([{ t: "2025-01-01", v: 100 }]); // true
 *
 * // Invalid: empty array
 * isValidSeries([]); // false
 *
 * // Invalid: NaN value
 * isValidSeries([{ t: "2025-01-01", v: NaN }]); // false
 */
export function isValidSeries(series) {
  return (
    Array.isArray(series) &&
    series.length > 0 &&
    series.every(
      (point) =>
        point !== null &&
        typeof point === "object" &&
        typeof point.t === "string" &&
        point.t.length > 0 &&
        typeof point.v === "number" &&
        !Number.isNaN(point.v)
    )
  );
}

/**
 * Creates a standardized validation error message for signals.
 *
 * @param {string} signalId - The signal identifier
 * @param {string} reason - The reason for validation failure
 * @returns {string} Formatted error message
 */
export function createSignalValidationError(signalId, reason) {
  return `[Signal:${signalId}] Validation failed: ${reason}`;
}

/**
 * Maps a raw value to a stress regime.
 * Generic helper for classifying stress levels.
 *
 * @param {number} score - Normalized score (0-100)
 * @returns {StressRegime} Stress regime classification
 */
export function scoreToRegime(score) {
  if (score < 15) return "CALM";
  if (score < 35) return "NORMAL";
  if (score < 55) return "WARNING";
  if (score < 75) return "STRESSED";
  return "CRISIS";
}

/**
 * Creates a SignalReading from signal output.
 * Helper for converting signal-specific output to standardized format.
 *
 * @param {string} id - Signal ID
 * @param {number} score - Normalized score (0-100)
 * @param {number} raw - Raw domain value
 * @param {number|null} [delta=null] - Change vs prior period
 * @param {string} [lastUpdated] - ISO timestamp (defaults to now)
 * @param {Object} [meta={}] - Additional metadata
 * @returns {SignalReading} Standardized reading
 */
export function createSignalReading(id, score, raw, delta = null, lastUpdated, meta = {}) {
  return {
    id,
    score: Math.round(Math.min(100, Math.max(0, score))),
    regime: scoreToRegime(score),
    raw,
    delta,
    lastUpdated: lastUpdated || new Date().toISOString(),
    meta,
  };
}

/**
 * Validates a SignalReading object.
 *
 * @param {SignalReading} reading - Reading to validate
 * @returns {boolean} True if valid
 */
export function isValidSignalReading(reading) {
  return (
    reading !== null &&
    typeof reading === "object" &&
    typeof reading.id === "string" &&
    typeof reading.score === "number" &&
    reading.score >= 0 &&
    reading.score <= 100 &&
    ["CALM", "NORMAL", "WARNING", "STRESSED", "CRISIS"].includes(reading.regime) &&
    typeof reading.raw === "number" &&
    (reading.delta === null || typeof reading.delta === "number") &&
    typeof reading.lastUpdated === "string"
  );
}
